import React, { useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import Logo from './Logo';

// Alert component
const Alert = ({ severity, children }) => {
  const styles = {
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  return (
    <div className={`px-4 py-3 border rounded-lg ${styles[severity]}`}>
      {children}
    </div>
  );
};

const ForgotPassword = ({ onBack, onResetSuccess }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await resetPassword({ username: email });
      setCodeSent(true);
      setSuccess(t('auth.code_sent_success'));
    } catch (err) {
      console.error('Password reset error:', err);
      if (err.name === 'UserNotFoundException') {
        setError(t('auth.error_user_not_found_reset'));
      } else if (err.name === 'LimitExceededException') {
        setError(t('auth.error_too_many_attempts'));
      } else {
        setError(t('auth.error_generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError(t('auth.error_passwords_mismatch'));
      return;
    }

    if (newPassword.length < 8) {
      setError(t('auth.error_password_too_short'));
      return;
    }

    setLoading(true);

    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: newPassword,
      });

      setSuccess(t('auth.password_reset_success'));

      setTimeout(() => {
        if (onResetSuccess) {
          onResetSuccess();
        } else {
          onBack();
        }
      }, 2000);
    } catch (err) {
      console.error('Password reset confirmation error:', err);
      if (err.name === 'CodeMismatchException') {
        setError(t('auth.error_code_mismatch'));
      } else if (err.name === 'ExpiredCodeException') {
        setError(t('auth.error_code_expired_reset'));
      } else if (err.name === 'InvalidPasswordException') {
        setError(t('auth.error_invalid_password_reset'));
      } else {
        setError(t('auth.error_generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm">
        <div className="card-elevated p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex justify-center">
              <Logo size={64} className="text-primary" />
            </div>
            <h1 className="text-xl font-bold text-primary mt-4">
              {t('auth.forgot_password_title')}
            </h1>
            <p className="text-sm text-text-muted mt-1">
              {codeSent
                ? t('auth.forgot_password_subtitle_code')
                : t('auth.forgot_password_subtitle')}
            </p>
          </div>

          {/* Send Code Form */}
          {!codeSent ? (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text-muted mb-1">
                  {t('auth.email')}
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  autoFocus
                  disabled={loading}
                  className="input"
                />
              </div>

              {error && <Alert severity="error">{error}</Alert>}
              {success && <Alert severity="success">{success}</Alert>}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    {t('auth.sending_code')}
                  </>
                ) : (
                  t('auth.send_code')
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-text-muted mb-1">
                  {t('auth.verification_code')}
                </label>
                <input
                  id="code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  autoFocus
                  disabled={loading}
                  className="input"
                  placeholder={t('auth.code_placeholder')}
                />
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-text-muted mb-1">
                  {t('auth.new_password')}
                </label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={loading}
                  className="input"
                />
                <p className="text-xs text-text-light mt-1">
                  {t('auth.min_password_length')}
                </p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-muted mb-1">
                  {t('auth.confirm_password')}
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                  className="input"
                />
              </div>

              {error && <Alert severity="error">{error}</Alert>}
              {success && <Alert severity="success">{success}</Alert>}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    {t('auth.resetting')}
                  </>
                ) : (
                  t('auth.reset_password_button')
                )}
              </button>

              {/* Resend Code */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setCodeSent(false);
                    setCode('');
                    setNewPassword('');
                    setConfirmPassword('');
                    setError('');
                    setSuccess('');
                  }}
                  disabled={loading}
                  className="text-sm text-primary hover:underline cursor-pointer"
                >
                  {t('auth.request_new_code')}
                </button>
              </div>
            </form>
          )}

          {/* Back to Login Link */}
          <div className="text-center mt-6">
            <button
              type="button"
              onClick={onBack}
              disabled={loading}
              className="text-sm text-primary hover:underline cursor-pointer flex items-center justify-center gap-1 mx-auto"
            >
              <ArrowLeft size={16} />
              {t('auth.back_to_login')}
            </button>
          </div>

          {/* Footer */}
          <div className="text-center mt-6">
            <p className="text-xs text-text-light opacity-70">
              © 2025 Holocron
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
